import * as moment from "moment";

import { Link } from "@teamprevent/common/task/taskActions/models";
import { CaseType, LinkObject } from "@teamprevent/common/model";

import { CustomerAccessGroupMode } from "@teamprevent/common/customer/models";

import { ALL_CUSTOMERS } from "../../calendar/clinics/models";
import { CustomerCategory } from "../customerCategories/model";
import { CustomerTeam } from "../customerTeam";

import { Location } from "redux-little-router";

export enum Sectors {
    NHS = "NHS",
    LocalAuthority = "LocalAuthority",
    Education = "Education",
    OtherPublicSector = "Other Public Sector",
    RetailAndDistribution = "Retail & Distribution",
    Construction = "Construction",
    Pharmaceuticals = "Pharmaceuticals",
    Other = "Other",
}

export const sectorOptions: { text: string; value: string }[] = [
    { text: "NHS", value: Sectors.NHS },
    { text: "Local Authority", value: Sectors.LocalAuthority },
    { text: "Education", value: Sectors.Education },
    { text: "Other Public Sector", value: Sectors.OtherPublicSector },
    { text: "Retail & Distribution", value: Sectors.RetailAndDistribution },
    { text: "Construction", value: Sectors.Construction },
    { text: "Pharmaceuticals", value: Sectors.Pharmaceuticals },
    { text: "Other", value: Sectors.Other }
];

export const accessGroupOptions: { text: string; value: string }[] = [
    { text: "Default", value: CustomerAccessGroupMode.Default },
    { text: "Group Only", value: CustomerAccessGroupMode.GroupOnly },
];

export const invoicingMethodOptions: { text: string; value: string }[] = [
    { text: "Automated", value: "Automated" },
    { text: "Manual", value: "Manual" },
];

export interface CustomerListModel {
    readonly id: string;
    readonly name: string;
    readonly prefix: string;
    readonly emailAddress: string;
    readonly active: boolean;
}

export interface CustomerDetailModel extends CustomerListModel {
    readonly id: string;
    readonly name: string;
    readonly customerCategory: Link;
    readonly prefix: string;
    readonly emailAddress: string;
    readonly phoneNumber: string;
    readonly customerNotes: string;
    readonly active: boolean;
    readonly products: CaseType[];
    readonly physioOrigins: string[];
    readonly covid19Features: string[];
    readonly assessmentAndWellBeingFeatures: string[];
    readonly isMultiFactorAuthenticationEnabled: boolean;
    readonly isEmployeeUserMultiFactorAuthenticationEnabled: boolean;
    readonly isDefaultCustomerType: boolean;
    readonly customerType: string;
    readonly partners: Link[];
    readonly addressLine1: string;
    readonly addressLine2: string;
    readonly addressLine3: string;
    readonly city: string;
    readonly county: string;
    readonly postCode: string;
    readonly domainName: string;
    readonly allowedEmailDomains: string[];
    readonly keyContacts: string;
    readonly accountCode: string;
    readonly allowanceStartDate: moment.Moment | undefined;
    readonly allowanceEndDate: moment.Moment | undefined;
    readonly contractCode: string;
    readonly invoicingMethod: "Automated" | "Manual" | null;
    readonly priceBucket: number;
    readonly sectors: string[];
    readonly financeCustomerName: string;
    readonly orgLevel1Billing: boolean;
    readonly ohdPaymentMethod: string;
    readonly ohDirectCustomer: boolean;
    readonly ssoCustomer: boolean;
    readonly useToyotaTemplates: boolean;
    readonly useOrgLevel1InMatchCriteria: boolean;
    readonly purchaseOrderNumber: string;
    readonly purchaseOrderRequiredPerCase: boolean;
    readonly subscribed: boolean;
    readonly accessGroupMode: string;
    readonly ppqAutomationEnabled: boolean;
    readonly hsNightWorkerAutomationEnabled: boolean;
    readonly genericCaseTypes: LinkObject[];
    readonly sncRule: LinkObject;
}

/**
 * A temporary type used to help with the transition from `Customer` to `CustomerDetailModel`.
 * Prefer `CustomerDetailModel` but use this type if you wish to use the `CustomerDetailModel`
 * but there is other referenced code that is still using `Customer` type or this `PartialCustomerDetailModel` type
 * and thus preventing the use of `CustomerDetailModel`.
 */
export type PartialCustomerDetailModel = Partial<Omit<CustomerDetailModel, "id">> & { id: string };

/**
 * A temporary type used to help with the transition from `Customer` to `CustomerListModel`.
 * Prefer `CustomerListModel` but use this type if you wish to use the `CustomerListModel`
 * but there is other referenced code that is still using `Customer` type or this `PartialCustomerListModel` type
 * and thus preventing the use of `CustomerListModel`.
 */
export type PartialCustomerListModel = Partial<Omit<CustomerListModel, "id">> & { id: string };

/**
 * @deprecated Something should really be either a list or a detail, not both. This is a legacy type we wish to remove. Replace with one of the following:
 * - `CustomerDetailModel` if you need the detail properties, can be retrieved using `useCustomerDetailsModel`
 * - `CustomerListModel` if you need just the list properties, can be retrieved using `useCustomerListModel`
 * - `PartialCustomerListModel` if you want to use the `CustomerListModel` properties but referenced code is still using `Customer` type
 * - `PartialCustomerDetailModel` if you want to use the `CustomerDetailModel` properties but referenced code is still using `Customer` type
 * - `CustomerInRedux` if you just want oh case as it comes from redux store
 */
export type Customer = (CustomerDetailModel | PartialCustomerDetailModel) & (CustomerListModel | PartialCustomerListModel);
export type PartialCustomerModelInRedux = PartialCustomerDetailModel & { readonly teams?: CustomerTeam[] };
type CustomerListModelInRedux = CustomerListModel & { listUpdated: moment.Moment };
export type CustomerDetailModelInRedux = CustomerDetailModel & { listUpdated: moment.Moment; detailUpdated: moment.Moment };
export type CustomerInRedux = PartialCustomerModelInRedux | CustomerListModelInRedux | CustomerDetailModelInRedux;

export interface CustomerCreateModel {
    id: string;
    name: string;
    customerCategory?: Link;
    prefix: string;
    emailAddress: string;
    phoneNumber: string;
    customerNotes: string;
    active: boolean;
    products: CaseType[];
    physioOrigins: string[];
    covid19Features: string[];
    assessmentAndWellBeingFeatures: string[];
    isMultiFactorAuthenticationEnabled: boolean;
    isEmployeeUserMultiFactorAuthenticationEnabled: boolean;
    isDefaultCustomerType: boolean;
    customerType: string;
    partners: Link[];
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    city: string;
    county: string;
    postCode: string;
    domainName: string;
    allowedEmailDomains: string[];
    keyContacts: string;
    accountCode: string;
    allowanceStartDate: moment.Moment;
    allowanceEndDate: moment.Moment;
    contractCode: string;
    sectors: string[];
    financeCustomerName: string;
    orgLevel1Billing: boolean;
    ohdPaymentMethod: string;
    ohDirectCustomer: boolean;
    useToyotaTemplates: boolean;
    useOrgLevel1InMatchCriteria: boolean;
    purchaseOrderNumber?: string;
    purchaseOrderRequiredPerCase: boolean;
    subscribed: boolean;
    accessGroupMode: string;
    sncRule: LinkObject;
}

export interface CustomerEditModel {
    id: string;
    name: string;
    customerCategory?: Link;
    prefix: string;
    emailAddress: string;
    phoneNumber: string;
    customerNotes: string;
    active: boolean;
    products: CaseType[];
    physioOrigins: string[];
    covid19Features: string[];
    assessmentAndWellBeingFeatures: string[];
    isMultiFactorAuthenticationEnabled: boolean;
    isEmployeeUserMultiFactorAuthenticationEnabled: boolean;
    isDefaultCustomerType: boolean;
    customerType: string;
    partners: Link[];
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    city: string;
    county: string;
    postCode: string;
    domainName: string;
    allowedEmailDomains: string[];
    keyContacts: string;
    accountCode: string;
    allowanceStartDate: moment.Moment;
    allowanceEndDate: moment.Moment;
    contractCode: string;
    sectors: string[];
    financeCustomerName: string;
    orgLevel1Billing: boolean;
    ohdPaymentMethod: string;
    ohDirectCustomer: boolean;
    useToyotaTemplates: boolean;
    useOrgLevel1InMatchCriteria: boolean;
    purchaseOrderNumber?: string;
    purchaseOrderRequiredPerCase: boolean;
    subscribed: boolean;
    accessGroupMode: string;
    sncRule: LinkObject;
}

export interface CustomerState {
    readonly customers: CustomerInRedux[];
    readonly customerCategories?: CustomerCategory[];
}

export type AppState = CustomerState & { router: Location };

export const getCustomerNamesForBookableCustomerIds = (customerIds: string[], customers: PartialCustomerListModel[]) => {
    const ids = customerIds ?? [];
    if (ids.length === 0 || ids.includes(ALL_CUSTOMERS)) {
        return ALL_CUSTOMERS;
    }
    const customersNames = customers?.filter(c => ids.includes(c.id))?.map(c => c.name)?.join(", ") ?? "";
    return customersNames;
};

export const getCustomerNamesForBillableCustomerIds = (customerIds: string[], customers: PartialCustomerListModel[]) => {
    const ids = customerIds ?? [];
    const customersNames = customers?.filter(c => ids.includes(c.id))?.map(c => c.name)?.join(", ") ?? "";
    return customersNames;
};
